import Matter from 'matter-js';
import React, { useCallback, useEffect, useRef } from 'react';

import logo from '../../../images/logo-on-gradient.png';

const Visualization = React.memo(
  ({ ballCount = 1 }) => {
    const containerRef = useRef(null);
    const balls = useRef([]); // Use ref to keep track of balls
    const colors = useRef(['#9E36F1', '#FFA12F', '#07C799', '#FF2059']).current;
    const intervalIdRef = useRef(null);

    // Define Matter.js engine and world outside of useEffect
    const engine = useRef(Matter.Engine.create()).current;
    const world = engine.world;

    // Create a separate composite for the logo
    const logoComposite = useRef(Matter.Composite.create()).current;

    // Function to add a new ball
    const addBall = useCallback(() => {
      const container = containerRef.current;
      const w = container.clientWidth;
      const size = Math.random() * (6 - 4) + 6;
      const x = Matter.Common.random(w / 2 + size, w / 2 - size);
      const y = Matter.Common.random(size, 0 - size * 100);
      const ball = Matter.Bodies.circle(x, y, size, {
        restitution: 0.5, // Increase restitution for bounciness
        render: {
          fillStyle: '#c4d3d3', // Default color
        },
      });
      ball.colorChanged = false; // Add a property to track color change
      Matter.Composite.add(world, ball);
      balls.current.push(ball);
    }, [world]);

    useEffect(() => {
      // module aliases
      const Render = Matter.Render,
        Runner = Matter.Runner,
        Bodies = Matter.Bodies,
        Composite = Matter.Composite,
        Events = Matter.Events;

      // create a renderer
      const container = containerRef.current;
      let h = container.clientHeight;
      let w = container.clientWidth;
      const render = Render.create({
        element: container,
        engine,
        options: {
          height: container.clientHeight,
          width: container.clientWidth,
          wireframes: false,
          background: 'none',
        },
      });
      Render.run(render);

      // create a runner
      const runner = Runner.create();
      Runner.run(runner, engine);

      // Add logo
      const imageAspectRatio = 245 / 45;
      let logoWidth = w;
      let logoHeight = logoWidth / imageAspectRatio;
      let logoX = w / 2;
      let logoY = h - 320;
      const logoBody = Bodies.rectangle(logoX, logoY, logoWidth, logoHeight, {
        isStatic: true,
        isSensor: true, // Make it a sensor
        render: {
          sprite: {
            texture: logo, // Use the imported image
            xScale: 0.33,
            yScale: 0.33,
            yOffset: 1,
          },
        },
      });
      Composite.add(logoComposite, logoBody);
      Composite.add(world, logoComposite);

      // create bounds
      const floor = Bodies.rectangle(w / 2, h - 220, w / 2, 20, {
        isStatic: true,
        render: { visible: false },
      });
      const fakeFloor = Bodies.rectangle(w / 2, h - 229, w / 2, 1, {
        isStatic: true,
        render: { fillStyle: '#c4d3d3' },
      });

      // add the floor to the world
      Composite.add(world, [floor, fakeFloor]);

      // Detect collisions and change ball color
      Events.on(engine, 'collisionStart', (event) => {
        const pairs = event.pairs;
        pairs.forEach((pair) => {
          const { bodyA, bodyB } = pair;
          if (bodyA === logoBody || bodyB === logoBody) {
            const ball = bodyA === logoBody ? bodyB : bodyA;
            if (balls.current.includes(ball) && !ball.colorChanged) {
              const newColor = colors[Math.floor(Math.random() * colors.length)];
              ball.render.fillStyle = newColor;
              ball.colorChanged = true; // Mark the ball as having its color changed
            }
          }
        });
      });

      // Remove balls that fall out of the canvas
      Events.on(engine, 'afterUpdate', function () {
        for (let i = balls.current.length - 1; i >= 0; i--) {
          if (balls.current[i].position.y > h + 100) {
            // Threshold to remove balls
            Matter.Composite.remove(world, balls.current[i]);
            balls.current.splice(i, 1);
          }
        }
      });

      // make responsive
      const resizeMatter = () => {
        w = render.canvas.width = container.clientWidth;
        h = render.canvas.height = container.clientHeight;

        Matter.Body.setPosition(floor, Matter.Vector.create(w / 2, h - 220));
        Matter.Body.setPosition(fakeFloor, Matter.Vector.create(w / 2, h - 229));

        // Recalculate the dimensions of the logo
        logoWidth = w;
        logoHeight = logoWidth / imageAspectRatio;

        // Update the logo's position
        Matter.Body.setPosition(logoBody, Matter.Vector.create(w / 2, h - 320));

        // Update the render options
        render.options.width = w;
        render.options.height = h;
        Render.lookAt(render, {
          min: { x: 0, y: 0 },
          max: { x: w, y: h },
        });
      };

      window.addEventListener('resize', resizeMatter);

      // Cleanup function to remove event listeners and Matter.js instances
      return () => {
        window.removeEventListener('resize', resizeMatter);
        Render.stop(render);
        Runner.stop(runner);
        Composite.clear(world, false);
        Matter.Engine.clear(engine);
        render.canvas.remove();
      };
    }, [engine, world, colors, logoComposite]); // Add dependencies to useEffect

    // Function to start adding balls
    const startAddingBalls = useCallback(() => {
      if (intervalIdRef.current === null) {
        intervalIdRef.current = setInterval(() => {
          for (let i = 0; i < ballCount; i++) {
            addBall();
          }
        }, 100); // Add balls every short interval based on ballCount
      }
    }, [ballCount, addBall]);

    // Function to stop adding balls
    const stopAddingBalls = useCallback(() => {
      if (intervalIdRef.current !== null) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
    }, []);

    useEffect(() => {
      startAddingBalls(); // Start adding balls when component mounts

      const handleVisibilityChange = () => {
        if (document.hidden) {
          stopAddingBalls();
        } else {
          startAddingBalls();
        }
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);

      // Cleanup function to remove event listeners and stop intervals
      return () => {
        stopAddingBalls();
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }, [startAddingBalls, stopAddingBalls]);

    return (
      <div
        data-analytics-component="calculator-visualization"
        style={{ position: 'absolute', inset: 0 }}
        ref={containerRef}
      />
    );
  },
  (prevProps, nextProps) => {
    // Only re-render if ballCount has changed
    return prevProps.ballCount === nextProps.ballCount;
  }
);

export default Visualization;
